import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowCounterClockwise, DownloadSimple, Trash } from "@phosphor-icons/react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/observer/AppHeader";
import { API_BASE, api, errMsg } from "@/lib/api";
import { useUser } from "@/lib/user";
import { btnGhost, inputCls, labelCls } from "@/lib/ui";
import { fmtDate, fmtHours, money } from "@/components/admin/format";

// Your own meetings: open the summary again, start the next one the same way, download the log, or delete it.
export default function Meetings() {
  const me = useUser();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    api.get("/sessions").then(({ data }) => setRows(data.filter((s) => s.owner === me.id))).catch((e) => toast.error(errMsg(e)));
  }, [me.id]);

  const remove = async (s) => {
    try {
      await api.delete(`/sessions/${s.id}`);
      setRows((r) => r.filter((x) => x.id !== s.id));
      toast.success("Meeting deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const shown = (rows || []).filter((s) => !q || `${s.title} ${s.purpose || ""}`.toLowerCase().includes(q.toLowerCase()));
  const hours = (rows || []).reduce((a, s) => a + (s.seconds || 0), 0);

  return (
    <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)]" data-testid="meetings-page">
      <AppHeader />
      <main className="relative z-10 max-w-5xl px-4 sm:px-10 pb-32">
        <div className="pt-12 pb-8 flex flex-wrap items-end justify-between gap-4 rise">
          <div>
            <p className={labelCls}>Your meetings</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">Meetings.</h1>
            {rows && rows.length > 0 && <p className="text-sm text-[var(--o-muted)] mt-3">{rows.length} saved · {fmtHours(hours)} of transcript. Only you and admins can see these.</p>}
          </div>
          <input className={`${inputCls} !w-56 text-xs`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label="Search meetings" data-testid="meetings-search" />
        </div>
        {!rows && <div className="h-40 shimmer" />}
        {rows && rows.length === 0 && (
          <div className="border border-[var(--o-border)] p-8 text-sm text-[var(--o-muted)]" data-testid="meetings-empty">
            No saved meetings yet. Private meetings are never saved, so they won't show here. <Link to="/" className="underline">Start one</Link>.
          </div>
        )}
        {rows && rows.length > 0 && shown.length === 0 && <p className="text-sm text-[var(--o-muted3)]">No meetings match.</p>}
        <div className="border border-[var(--o-border)] divide-y divide-[var(--o-border-soft)]">
          {shown.map((s) => (
            <div key={s.id} className="px-5 py-4 grid md:grid-cols-[1fr_auto] gap-x-6 gap-y-3 items-center" data-testid={`meeting-row-${s.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Link to={`/session/${s.id}`} className="font-semibold truncate hover:underline" data-testid={`meeting-open-${s.id}`}>{s.title}</Link>
                  {s.live_in_memory && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-[#10B981]/50 text-[#10B981]">running</span>}
                  {s.mode === "replay" && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-[#F59E0B]/40 text-[#F59E0B]">replay</span>}
                </div>
                <div className="font-mono text-[11px] text-[var(--o-muted3)] mt-0.5">
                  {fmtDate(s.created_at)} · {fmtHours(s.seconds)} · {s.answers} answer{s.answers === 1 ? "" : "s"}{s.cost != null ? ` · ${money(s.cost)}` : ""}
                </div>
                {s.purpose && <p className="text-xs text-[var(--o-muted2)] mt-1 line-clamp-1">{s.purpose}</p>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => nav(`/?again=${s.id}`)} className={`${btnGhost} !px-2.5 !py-1.5 text-xs`} title="Start a new meeting with the same title, focus, people and background" data-testid={`meeting-again-${s.id}`}>
                  <ArrowCounterClockwise size={13} /> Start again
                </button>
                <a href={`${API_BASE}/sessions/${s.id}/download`} className={`${btnGhost} !px-2.5 !py-1.5 text-xs`} title="Download the full log as JSON" data-testid={`meeting-download-${s.id}`}>
                  <DownloadSimple size={13} /> Log
                </a>
                <button type="button" onClick={() => setConfirm(s)} disabled={s.live_in_memory} className={`${btnGhost} !px-2.5 !py-1.5 text-xs text-[#EF4444]`} title={s.live_in_memory ? "End the meeting first" : "Delete the log and any audio still kept"} data-testid={`meeting-delete-${s.id}`}>
                  <Trash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[var(--o-bg2)] border-[var(--o-border)] rounded-none" data-testid="meeting-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete “{confirm?.title}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--o-muted)]">
              The transcript, answers, chapters and board are removed from this server, along with any audio still kept for it. Nothing can be recovered afterwards. The AI services used during the meeting keep whatever their own terms say.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[var(--o-border)] bg-transparent">Keep it</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-[#EF4444] text-white hover:opacity-90" onClick={() => remove(confirm)} data-testid="meeting-confirm-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
